// Vendor
import * as React from 'react';
import { SyntheticEvent } from 'react';

// Bernie
import { ILocalization, LocalizedText, LocalizedTextProps } from 'bernie-l10n';
import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';

// Stores
import { MultiItemStore } from 'stores/multi-item-store';

// Models
import { CartRatePlan, InCartProperty } from 'models/multi-item/multi-item-types';

// UITK
import { UitkCard, UitkCardContent, UitkCardImageLayout, UitkCardMedia, UitkCardTextSegment } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkLinkButton } from 'uitk-react-button';

const LinkButtonTracked = ClickTracker(UitkLinkButton);

const guestSummaryMessage = (rooms: number, guests: number): LocalizedTextProps => {
  if (rooms === 1) {
    if (guests === 1) {
      return {
        message: 'multiProperty.guestSummary.singleRoomSingleGuest',
      };
    }

    return {
      message: 'multiProperty.guestSummary.singleRoomMultipleGuests',
      data: [guests],
    };
  }

  return {
    message: 'multiProperty.guestSummary.multipleRoomsMultipleGuests',
    data: [rooms, guests],
  };
};

const renderChildren = (children: number, l10n: ILocalization): JSX.Element => {
  if (children) {
    if (children > 1) {
      return <>, {l10n.formatText('hotels.roomPicker.count.children', children)}</>;
    }

    return <>, {l10n.formatText('hotels.roomPicker.count.child')}</>;
  }

  return null;
};

export interface CartItemCardProps {
  adults: number;
  children: number;
  getDisplayPrice: () => any;
  l10n?: ILocalization;
  ratePlan: CartRatePlan;
  rooms?: number;
}

export interface CartItemsProps {
  cartItemDeleter: (id) => () => void;
  removeText: string;
  isFirstItem: boolean;
  analytics?: AnalyticsStore;
  multiItem: MultiItemStore;
  itemIndex?: number;
  l10n?: ILocalization;
  routeStore: RouteStore;
  useRewards: string;
  scrollAndRoute: (routeStore, roomIndex, businessModelFilter) => void;
}

export function replaceMixAndMatchUrl(
  roomIndex: number,
  multiItem: MultiItemStore,
  routeStore: RouteStore,
  scrollAndRoute: (routeStore, roomIndex, businessModelFilter) => void
): (e?: SyntheticEvent) => void {
  const businessModelFilter = multiItem.cartInfo && multiItem.cartInfo.type;

  return (e?: SyntheticEvent) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }

    scrollAndRoute(routeStore, roomIndex, businessModelFilter);
  };
}

export const MultiPropCard = ({ adults, children, getDisplayPrice, ratePlan, rooms }: CartItemCardProps): JSX.Element => (
  <>
    <div className="uitk-cell uitk-type-200 truncate">{ratePlan.roomName}</div>
    <div className="uitk-cell uitk-type-200">
      <LocalizedText {...guestSummaryMessage(rooms, adults + children)} />
    </div>
    <div className="uitk-cell uitk-type-300 uitk-type-bold all-t-padding-two">
      <LocalizedText message="multiProperty.price.perNight" data={[getDisplayPrice()]} />
    </div>
    <div className="uitk-type-100">
      <LocalizedText message="multiProperty.priceSummary.perRoom" />
    </div>
  </>
);

export const MixMatchCard = ({ adults, children, getDisplayPrice, ratePlan, l10n }: CartItemCardProps): JSX.Element => (
  <>
    <h3 className="uitk-cell uitk-type-400 pwa-theme--grey-900 truncate" data-stid="content-hotel-title">
      {ratePlan.roomName}
    </h3>
    <div className="uitk-cell uitk-type-200" data-stid="room-summary">
      {adults > 1 ? (
        <>{l10n.formatText('hotels.roomPicker.count.adults', adults)}</>
      ) : (
        <>{l10n.formatText('hotels.roomPicker.count.adult')}</>
      )}
      {renderChildren(children, l10n)}
    </div>
    <div className="uitk-cell uitk-type-300 uitk-type-bold all-t-padding-two">{[getDisplayPrice()]}</div>
    <div className="uitk-type-100">
      <LocalizedText message="hotels.infosite.paymentOptions.perNight.text" />
    </div>
  </>
);

export const InCartListing = ({
  adults,
  cartItemDeleter,
  children,
  id,
  image,
  isFirstItem,
  multiItem,
  routeStore,
  name,
  ratePlan,
  removeText,
  rooms,
  analytics,
  l10n,
  itemIndex,
  offerIndex,
  useRewards,
  scrollAndRoute,
}: InCartProperty & CartItemsProps): JSX.Element => {
  const getDisplayPrice = () => {
    if (!ratePlan || !ratePlan.available || !ratePlan.leadPrice || !ratePlan.leadPrice.formatted) {
      return null;
    }

    if (hasLoyaltyPoints()) {
      return ratePlan.priceAfterLoyaltyPointsApplied.formatted;
    }

    return ratePlan.leadPrice.formatted;
  };

  const hasLoyaltyPoints = () =>
    ((isFirstItem && !multiItem.showMixMatchExperience) || (offerIndex === 0 && multiItem.showMixMatchExperience)) &&
    useRewards === 'true' &&
    ratePlan.priceAfterLoyaltyPointsApplied &&
    ratePlan.priceAfterLoyaltyPointsApplied.formatted;

  const fees = ratePlan && ratePlan.fees;
  const trackRemoveSectionClick = () => {
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('MultiProp.ClickedRemoveSection', 'MultiProp Property Remove Attempt', null, null, true);
    }
  };

  return (
    <UitkCard imageLayout={UitkCardImageLayout.LEFT_FULLBLEED} withBorder={true}>
      <UitkCardMedia>
        <UitkFigure ratio={UitkFigureAspectRatioType.R3_2}>
          <UitkImage alt={name} src={image} />
        </UitkFigure>
      </UitkCardMedia>
      <UitkCardContent>
        <UitkCardTextSegment className="uitk-cell all-cell-fill">
          <h3 className="uitk-cell uitk-type-400 pwa-theme--grey-900 truncate" data-stid="content-hotel-title">
            {name}
          </h3>
          {getDisplayPrice() ? (
            <>
              {multiItem.showMixMatchExperience ? (
                <MixMatchCard adults={adults} children={children} getDisplayPrice={getDisplayPrice} ratePlan={ratePlan} l10n={l10n} />
              ) : (
                <MultiPropCard adults={adults} children={children} getDisplayPrice={getDisplayPrice} ratePlan={ratePlan} rooms={rooms} />
              )}
              {hasLoyaltyPoints() && (
                <div className="uitk-type-100">
                  <LocalizedText message="common.pointsApplied" />
                </div>
              )}
              {fees && fees.length > 0 && (
                <div className="all-t-padding-two">
                  {fees.map((fee, index) => (
                    <div key={index} className="uitk-cell uitk-type-100">
                      {fee}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="uitk-cell uitk-type-200  pwa-theme--urgency all-t-padding-two">
              <LocalizedText message="multiProperty.playback.propertyNotAvailable" />
            </div>
          )}
        </UitkCardTextSegment>
      </UitkCardContent>
      <section className="uitk-cell all-cell-1-1 uitk-grid uitk-type-left uitk-card-separator-top" onClick={trackRemoveSectionClick}>
        {multiItem.showMixMatchExperience ? (
          <LinkButtonTracked
            className="uitk-cell all-cell-shrink all-x-padding-two all-y-padding-two"
            onClick={replaceMixAndMatchUrl(offerIndex, multiItem, routeStore, scrollAndRoute)}
            rfrr={`MixMatch.ReplacePropertyClick.${offerIndex}`}
            linkName="Multi Property replace button"
          >
            <span>{removeText}</span>
            <span className="is-visually-hidden">{name}</span>
          </LinkButtonTracked>
        ) : (
          <LinkButtonTracked
            className="uitk-cell all-cell-shrink all-x-padding-two all-y-padding-two"
            onClick={cartItemDeleter(id)}
            rfrr={`MultiProp.RemovePropertyClick.${itemIndex}`}
            linkName="Multi Property remove button"
          >
            <span>{removeText}</span>
            <span className="is-visually-hidden">{name}</span>
          </LinkButtonTracked>
        )}
      </section>
    </UitkCard>
  );
};
